import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import type { AspectRatio } from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';

import { AspectRatioModal } from 'js/src/components/common/AspectRatioModal';
import { getSupportedAspectRatios } from 'js/src/data/templates/templates-selectors';
import { getVideoDraftById } from 'js/src/data/video-planner';
import type { RootState } from 'js/src/libs/RootState';

interface VideoDraftAspectRatioModalProps {
    videoDraftId: string;
    sendMessage: (message: string) => Promise<boolean>;
    isOpen: boolean;
    onClose: () => void;
}

export const VideoDraftAspectRatioModal: React.FC<VideoDraftAspectRatioModalProps> = ({
    videoDraftId,
    sendMessage,
    isOpen,
    onClose,
}) => {
    const videoDraft = useSelector((state: RootState) => getVideoDraftById(state, videoDraftId));

    const templateId = videoDraft?.luminary.template?.id;
    const supportedAspectRatios = useSelector((state: RootState) =>
        templateId ? getSupportedAspectRatios(state, templateId) : null,
    );

    const availableAspectRatios = useMemo(
        () => supportedAspectRatios || VideoData.ALL_ASPECT_RATIOS,
        [supportedAspectRatios],
    );

    const initialAspectRatio = useMemo(
        () => videoDraft?.luminary.aspectRatio || availableAspectRatios[0],
        [videoDraft?.luminary.aspectRatio, availableAspectRatios],
    );

    const handleApply = useCallback(
        (selectedAspectRatio: AspectRatio) => {
            if (!videoDraft) return;
            void sendMessage(`Change aspect ratio to ${selectedAspectRatio}`);

            analytics.track('Sketch: Video Action - Change Aspect Ratio', {
                video_title: videoDraft.name,
                aspect_ratio: selectedAspectRatio,
            });

            onClose();
        },
        [sendMessage, videoDraft, onClose],
    );

    if (!videoDraft) {
        return null;
    }

    return (
        <AspectRatioModal
            isOpen={isOpen}
            onClose={onClose}
            onApply={handleApply}
            initialAspectRatio={initialAspectRatio}
            availableAspectRatios={availableAspectRatios}
            title="Change aspect ratio"
            description="Select the aspect ratio for your video. Different aspect ratios are optimized for different platforms."
        />
    );
};
